import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { StorageService } from '../../../../../core/services/storage/storage.service';
import { User } from '../../../../../core/models/user/user';
import { UserService } from '../../../../../core/services/user/user.service';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UnsubAbonnementComponent } from '../unsub-abonnement/unsub-abonnement.component';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { Router } from '@angular/router';
import { PaypalPremiumComponent } from '../../../../../shared/components/paypal-premium/paypal-premium.component';
import { PaypalEssentielComponent } from '../../../../../shared/components/paypal-essentiel/paypal-essentiel.component';
import { PaypalProComponent } from '../../../../../shared/components/paypal-pro/paypal-pro.component';
import { PaypalService } from '../../../../../core/services/register/servicesPaypal/paypal/paypal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SubscriptionCommandHandlerService } from '../../../../../core/services/register/servicesPaypal/subscriptionCommandHandler/subscription-command-handler.service';

@Component({
    selector: 'app-edit-version',
    templateUrl: './edit-version.component.html',
    styleUrls: ['./edit-version.component.scss'],
    imports: [CommonModule, FormsModule, TranslateModule, NgIf, PaypalPremiumComponent, PaypalEssentielComponent, PaypalProComponent]
})
export class EditVersionComponent implements OnInit, OnDestroy {
  private alertService = inject(AlertService);
  protected modal = inject(NgbActiveModal);
  private storageService = inject(StorageService);
  private userService = inject(UserService);
  private router = inject(Router);
  private paypalService = inject(PaypalService);
  private subscriptionCommandHandlerService = inject(SubscriptionCommandHandlerService);


  private destroyRef = inject(DestroyRef);

  protected User: User | undefined = this.storageService.getUser();
  protected showPaypal = false;
  protected selectedVersion = '';
  private billingPeriod: 'monthly' | 'annual' = 'monthly'; // Default to monthly
  protected checkRythmeAbonnement: 'ESSENTIEL_MENSUEL' | 'ESSENTIEL_ANNUEL' | 'PREMIUM_MENSUEL' | 'PREMIUM_ANNUEL' | 'PRO_MENSUEL' | 'PRO_ANNUEL' = 'PREMIUM_MENSUEL';
  protected hasPendingSubscription = false;
  private userRefreshInterval: ReturnType<typeof setInterval> | null = null;
  private initialProfileName = '';

  ngOnInit() {
    // Reset modal state to ensure it starts from the beginning
    this.showPaypal = false;
    this.selectedVersion = '';
    
    // Clean up old subscription date from localStorage (keep version change block)
    localStorage.removeItem('subscriptionDate');
    
    // Reset pending subscription status
    this.hasPendingSubscription = false;
    
    // Refresh user data from server to ensure we have latest subscriptions
    this.userService.getCurrentUser().subscribe({
      next: (user) => {
        if (user) {
          this.storageService.setUser(user);
          this.User = user;
        }
        this.initializeComponent();
      },
      error: () => {
        // If server call fails, use cached user
        this.initializeComponent();
      }
    });
  }
  
  private initializeComponent(): void {
    // Store initial profile name to detect changes
    this.initialProfileName = this.User?.profile?.name || '';
    
    // Always default to monthly billing period when opening the modal
    // This allows users to easily switch to annual if they want, but monthly is the default
    this.billingPeriod = 'monthly';
    
    // Initialize payment mode based on current user type (default to monthly)
    if (this.isEssentielUser) {
      this.checkRythmeAbonnement = 'ESSENTIEL_MENSUEL';
    } else if (this.isPremiumUser) {
      this.checkRythmeAbonnement = 'PREMIUM_MENSUEL';
    } else if (this.isProUser) {
      this.checkRythmeAbonnement = 'PRO_MENSUEL';
    }
    
    // Start polling for user updates when PayPal is shown
    this.startUserRefreshPolling();
  }
  
  ngOnDestroy() {
    this.stopUserRefreshPolling();
  }
  
  private startUserRefreshPolling() {
    // Poll every 3 seconds to check for user profile updates
    this.userRefreshInterval = setInterval(() => {
      if (this.showPaypal) {
        this.refreshUser();
      }
    }, 3000);
  }
  
  private stopUserRefreshPolling() {
    if (this.userRefreshInterval) {
      clearInterval(this.userRefreshInterval);
      this.userRefreshInterval = null;
    }
  }
  
  private refreshUser() {
    this.userService.getCurrentUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedUser: User) => {
          const newProfileName = updatedUser?.profile?.name || '';
          
          // If profile changed, update local user and notify
          if (newProfileName !== this.initialProfileName && newProfileName !== this.User?.profile?.name) {
            this.storageService.setUser(updatedUser);
            this.User = updatedUser;
            this.initialProfileName = newProfileName;
            
            // Clear PayPal pending flag
            localStorage.removeItem('paypal_subscription_pending');
            localStorage.removeItem('paypal_expected_profile');
            
            // Trigger custom event to update header and other components
            window.dispatchEvent(new CustomEvent('userUpdated'));
            
            // Close PayPal view and show success
            this.showPaypal = false;
            
            // Show success message
            this.alertService.open({
              header: 'Succès',
              content: `Votre version a été mise à jour avec succès. Vous êtes maintenant en version ${newProfileName}.`,
              contentType: 'text'
            });
            
            // Stop polling
            this.stopUserRefreshPolling();
          }
        },
        error: () => {
          // Silently fail
        }
      });
  }
  
  closeModal(): void {
    this.modal.dismiss('close');
  }
  
  readonly isBlocked = false;

  // User type methods
  get isPremiumGiftedUser(): boolean {
    return this.userService.isPremiumGiftedUser(this.User);
  }

  get isStandardUser(): boolean {
    // Premium Découverte should be treated as Basic/Standard in edit version
    if (this.isPremiumDiscoveryUser) {
      return true;
    }
    return this.userService.isStandardUser(this.User);
  }

  get isPremiumUser(): boolean {
    return this.userService.isPremiumUser(this.User);
  }

  public isStandardAutoImportUser(): boolean {
    return this.userService.isStandardAutoImportUser(this.User);
  }

  get isPremiumDiscoveryUser(): boolean {
    return this.userService.isPremiumDiscoveryUser(this?.User);
  }

  get isEssentielUser(): boolean {
    return this.userService.isEssentielUser(this?.User);
  }

  get isProUser(): boolean {
    return this.userService.isProUser(this?.User);
  }

  get hasAnnualSubscription(): boolean {
    return this.userService.hasAnnualSubscription(this?.User);
  }

  public hasMonthlySubscription(): boolean {
    return this.userService.hasMonthlySubscription(this?.User);
  }

  // Billing period methods
  get isAnnualBilling(): boolean {
    return this.billingPeriod === 'annual';
  }

  setBillingPeriod(period: 'monthly' | 'annual'): void {
    this.billingPeriod = period;
    this.updatePaymentMode();
    // If a version is already selected, update it
    if (this.selectedVersion) {
      this.updatePaymentMode();
    }
  }

  private updatePaymentMode(): void {
    if (this.selectedVersion === 'ESSENTIEL') {
      this.checkRythmeAbonnement = this.isAnnualBilling ? 'ESSENTIEL_ANNUEL' : 'ESSENTIEL_MENSUEL';
    } else if (this.selectedVersion === 'PREMIUM') {
      this.checkRythmeAbonnement = this.isAnnualBilling ? 'PREMIUM_ANNUEL' : 'PREMIUM_MENSUEL';
    } else if (this.selectedVersion === 'PRO') {
      this.checkRythmeAbonnement = this.isAnnualBilling ? 'PRO_ANNUEL' : 'PRO_MENSUEL';
    }
  }

  // Price methods
  get essentielPrice(): string {
    return this.isAnnualBilling ? '32,90' : '3,35';
  }

  get premiumPrice(): string {
    return this.isAnnualBilling ? '47,90' : '4,85';
  }

  get proPrice(): string {
    return this.isAnnualBilling ? '67,90' : '6,85';
  }

  // Check if user already has the same subscription with the same billing period
  hasSameSubscription(version: string, billingPeriod: 'monthly' | 'annual'): boolean {
    // Premium Découverte users should be able to subscribe to Premium monthly
    // They don't have an active subscription, so they should not be blocked
    if (this.isPremiumDiscoveryUser && version.toUpperCase() === 'PREMIUM') {
      return false;
    }
    
    const versionUpper = version.toUpperCase();
    const isAnnual = billingPeriod === 'annual';
    
    // First check: verify with active subscriptions only
    if (!this.User?.subscriptions) {
      return false;
    }
    
    const activeSubscriptions = this.User.subscriptions.filter(s => !s.deleted);
    
    // Check if user has an active subscription of the same type AND same billing period
    const hasSameTypeAndBilling = activeSubscriptions.some(sub => {
      const subType = (sub.type || '').toUpperCase();
      
      // Match subscription type (PRO, PREMIUM, ESSENTIEL)
      if (subType !== versionUpper) {
        return false;
      }
      
      // Check if billing period matches
      const subAmount = parseFloat(sub.amount || '0');
      // PRO annual: 67,90, monthly: 6,35
      // PREMIUM annual: 47,90, monthly: 4,85
      // ESSENTIEL annual: 32,90, monthly: 3,35
      const isSubAnnual = subAmount > 15; // Annual subscriptions are > 15
      
      return isSubAnnual === isAnnual;
    });
    
    return hasSameTypeAndBilling;
  }

  // Version selection with PayPal integration
  selectVersionForPaypal(version: string): void {
    // Prevent selecting the same subscription with the same billing period
    if (this.hasSameSubscription(version, this.billingPeriod)) {
      this.alertService.open({
        header: 'Information',
        content: 'Vous disposez déjà de cet abonnement avec ce rythme de paiement.',
        contentType: 'text'
      });
      return;
    }
    
    this.selectedVersion = version;
    this.updatePaymentMode();
    // Store initial profile before showing PayPal
    this.initialProfileName = this.User?.profile?.name || '';
    // Mark that a PayPal subscription change is in progress
    localStorage.setItem('paypal_subscription_pending', 'true');
    localStorage.setItem('paypal_expected_profile', version);
    this.showPaypal = true;
    // Polling is already started in ngOnInit
  }

  downgradeToStandard(): void {
    // Use UnsubAbonnementComponent which handles the cancellation properly
    // It works for all subscription types by checking the user's current subscriptions
    const unsubModal = this.alertService.openWithContent(UnsubAbonnementComponent);
    
    unsubModal.result
      .then((result) => {
        // Update local user
        if (result) {
          this.User = this.storageService.getUser();
        }
        // Close the edit-version modal
        this.modal.dismiss(result);
      })
      .catch((event) => {
        // User cancelled or error occurred
        this.modal.dismiss(event);
      });
  }

  // Subscribe methods - now redirect to PayPal
  public subscribeToEssentiel() {
    this.selectVersionForPaypal('ESSENTIEL');
  }

  public subscribeToPremium() {
    this.selectVersionForPaypal('PREMIUM');
  }

  public subscribeToPro() {
    this.selectVersionForPaypal('PRO');
  }

  // Methods to switch to annual billing
  public switchToAnnualEssentiel() {
    this.setBillingPeriod('annual');
    this.selectVersionForPaypal('ESSENTIEL');
  }

  public switchToAnnualPremium() {
    this.setBillingPeriod('annual');
    this.selectVersionForPaypal('PREMIUM');
  }

  public switchToAnnualPro() {
    this.setBillingPeriod('annual');
    this.selectVersionForPaypal('PRO');
  }

  // Methods to switch to monthly billing
  public switchToMonthlyEssentiel() {
    this.setBillingPeriod('monthly');
    this.selectVersionForPaypal('ESSENTIEL');
  }

  public switchToMonthlyPremium() {
    this.setBillingPeriod('monthly');
    this.selectVersionForPaypal('PREMIUM');
  }

  public switchToMonthlyPro() {
    this.setBillingPeriod('monthly');
    this.selectVersionForPaypal('PRO');
  }



  // Unsubscribe methods
  public unsubscribeToPremium() {
    this.alertService.openWithContent(UnsubAbonnementComponent).result
      .then((result) => {
        // Mettre à jour l'utilisateur local
        if (result) {
          this.User = this.storageService.getUser();
        }
        this.modal.dismiss(result);
      })
      .catch((event) => {
        this.modal.dismiss(event);
      });
  }

  public unsubscribeToEssentiel() {
    const changeProfileCallback = {
      next: (response: User) => {
        this.storageService.setUser(response);
        this.User = this.storageService.getUser();
        this.modal.dismiss(response);

        // Trigger custom event to update header
        window.dispatchEvent(new CustomEvent('userUpdated'));

        // Show success message
        this.alertService.open({
          header: 'Confirmation',
          content: 'La version de votre compte a été modifiée. Votre compte est maintenant en version "Standard" et votre souscription a été annulée.',
          contentType: 'text'
        });
      },
      error: () => {
        this.alertService.open({
          header: 'Erreur',
          content: 'Une erreur est survenue lors de l\'annulation de votre abonnement. Veuillez réessayer.',
          contentType: 'text'
        });
      },
    };

    const essentielSubscriptions = this.User?.subscriptions?.filter((s) => s.type == 'ESSENTIEL' && !s.deleted) || [];
    if (essentielSubscriptions.length > 0) {
      this.paypalService
        .cancelPaypalSubscription(essentielSubscriptions[0].subscriptionId, "simple", this.User?.idUser)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(changeProfileCallback);
    } else {
      this.alertService.open({
        header: 'Erreur',
        content: 'Aucun abonnement Essentiel actif trouvé.',
        contentType: 'text'
      });
    }
  }

  public unsubscribeToPro() {
    const changeProfileCallback = {
      next: (response: User) => {
        this.storageService.setUser(response);
        this.User = this.storageService.getUser();
        this.modal.dismiss(response);

        // Trigger custom event to update header
        window.dispatchEvent(new CustomEvent('userUpdated'));

        // Show success message
        this.alertService.open({
          header: 'Confirmation',
          content: 'La version de votre compte a été modifiée. Votre compte est maintenant en version "Standard" et votre souscription a été annulée.',
          contentType: 'text'
        });
      },
      error: () => {
        this.alertService.open({
          header: 'Erreur',
          content: 'Une erreur est survenue lors de l\'annulation de votre abonnement. Veuillez réessayer.',
          contentType: 'text'
        });
      },
    };

    const proSubscriptions = this.User?.subscriptions?.filter((s) => s.type == 'PRO' && !s.deleted) || [];
    if (proSubscriptions.length > 0) {
      this.paypalService
        .cancelPaypalSubscription(proSubscriptions[0].subscriptionId, "simple", this.User?.idUser)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(changeProfileCallback);
    } else {
      this.alertService.open({
        header: 'Erreur',
        content: 'Aucun abonnement Pro actif trouvé.',
        contentType: 'text'
      });
    }
  }

}